/**
 * Stock-screen financial snapshot (ADR-0034 · ADR-0035 · ADR-0036). Point-in-time
 * ratios plus annual statement years. Every metric is nullable: a missing ROE is
 * not an ROE of zero (R2b).
 */
import { z } from 'zod';

const NullableNumberSchema = z.number().nullable();
const NullableNonnegSchema = z.number().nonnegative().nullable();
const FiscalYearSchema = z.number().int().gte(1990).lte(2100);

/** Serving cap on annual periods returned on the profile (income, balance, cash-flow). */
export const STATEMENT_YEARS_MAX = 8;

export const StockRatiosSchema = z.object({
  epsTtm: NullableNumberSchema,
  peFwd: NullableNumberSchema,
  grossMargin: NullableNumberSchema,
  operatingMargin: NullableNumberSchema,
  profitMargin: NullableNumberSchema,
  roe: NullableNumberSchema,
  roa: NullableNumberSchema,
  pb: NullableNumberSchema,
  de: NullableNumberSchema,
  currentRatio: NullableNonnegSchema,
  dividendRate: NullableNonnegSchema,
  payoutRatio: NullableNumberSchema,
  totalCash: NullableNonnegSchema,
  totalDebt: NullableNonnegSchema,
  freeCashFlow: NullableNumberSchema,
  operatingCashFlow: NullableNumberSchema,
});
export type StockRatios = z.infer<typeof StockRatiosSchema>;

export const IncomePeriodSchema = z.object({
  year: FiscalYearSchema,
  revenue: NullableNonnegSchema,
  netIncome: NullableNumberSchema,
  /**
   * Fiscal-year cell (ADR-0036). Absent and null are equivalent: no FY
   * observation. Consumers must not distinguish pre-cutover omission from a
   * present-but-NULL column; both fall through to TTM where the UI allows it.
   */
  grossMargin: NullableNumberSchema.optional(),
  operatingMargin: NullableNumberSchema.optional(),
  profitMargin: NullableNumberSchema.optional(),
  eps: NullableNumberSchema.optional(),
  ebit: NullableNumberSchema.optional(),
});
export type IncomePeriod = z.infer<typeof IncomePeriodSchema>;

export const BalancePeriodSchema = z.object({
  year: FiscalYearSchema,
  totalAssets: NullableNonnegSchema,
  totalLiabilities: NullableNonnegSchema,
  equity: NullableNumberSchema,
  cash: NullableNonnegSchema,
  totalDebt: NullableNonnegSchema,
  currentAssets: NullableNonnegSchema,
  currentLiabilities: NullableNonnegSchema,
  retainedEarnings: NullableNumberSchema,
  goodwill: NullableNonnegSchema,
  longTermDebt: NullableNonnegSchema,
});
export type BalancePeriod = z.infer<typeof BalancePeriodSchema>;

/**
 * Annual cash-flow cells. Values are stored as reported (signed). Local
 * synthetic uses positive outflow magnitudes for capex/dividendsPaid/buybacks;
 * live vendor files may carry negatives. The schema does not flip a sign.
 * When both legs are present and capex is a positive magnitude,
 * freeCashFlow = operating − capex. Investing and financing stay signed
 * (typically negative). A missing capex is null, never a negated operating
 * residual (R2b).
 */
export const CashFlowPeriodSchema = z.object({
  year: FiscalYearSchema,
  operating: NullableNumberSchema,
  investing: NullableNumberSchema,
  financing: NullableNumberSchema,
  freeCashFlow: NullableNumberSchema,
  capex: NullableNumberSchema,
  dividendsPaid: NullableNumberSchema,
  buybacks: NullableNumberSchema,
  debtRepaid: NullableNumberSchema,
  debtIssued: NullableNumberSchema,
  netChangeCash: NullableNumberSchema,
});
export type CashFlowPeriod = z.infer<typeof CashFlowPeriodSchema>;

/**
 * Statement years are ordered oldest-first with distinct fiscal years. Charts
 * position bars by array index, so a descending payload would render
 * right-to-left with every comparison silently suppressed.
 */
function assertAscendingDistinctYears(
  periods: readonly { readonly year: number }[],
  ctx: z.RefinementCtx,
): void {
  for (let index = 1; index < periods.length; index += 1) {
    const previous = periods[index - 1];
    const current = periods[index];
    if (previous === undefined || current === undefined) return;
    if (current.year <= previous.year) {
      ctx.addIssue({
        code: 'custom',
        path: [index, 'year'],
        message: 'statement years must be ordered oldest-first with distinct fiscal years',
      });
    }
  }
}

function statementYears<T extends z.ZodType<{ readonly year: number }>>(schema: T) {
  return z.array(schema).max(STATEMENT_YEARS_MAX).superRefine(assertAscendingDistinctYears);
}

export const StockFinancialsSchema = z.object({
  ratios: StockRatiosSchema,
  income: statementYears(IncomePeriodSchema),
  /**
   * Additive (ADR-0035). `.default([])` so a pre-cutover profile-v2 body still
   * parses as empty-honest series rather than failing the stock screen.
   */
  balanceSheet: statementYears(BalancePeriodSchema).default([]),
  cashFlow: statementYears(CashFlowPeriodSchema).default([]),
});
export type StockFinancials = z.infer<typeof StockFinancialsSchema>;
